import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { AnimatePresence } from 'framer-motion';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Portfolio from './pages/Portfolio';
import ProjectDetail from './pages/ProjectDetail';
import ServiceDetail from './pages/ServiceDetail';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Cursor from './components/Cursor';

// Hooks
import useIsMobile from './hooks/useIsMobile';

// Styles
import './App.css';

// Apple-inspired Theme
const theme = {
  colors: {
    primary: '#007AFF',           // Apple blue
    primaryDark: '#0056CC',       
    secondary: '#1D1D1F',         // Apple dark gray
    accent: '#FF3B30',            // Apple red
    background: '#FFFFFF',        // Pure white
    backgroundAlt: '#F2F2F7',     // Apple light gray
    backgroundDark: '#000000',    // Pure black
    light: '#FFFFFF',
    dark: '#1D1D1F',             
    text: '#1D1D1F',             // Apple primary text
    textLight: '#86868B',        // Apple secondary text  
    textMedium: '#515154',       // Apple tertiary text
    success: '#30D158',          // Apple green
    warning: '#FF9500',          // Apple orange
    error: '#FF3B30',            // Apple red
    surface: '#F2F2F7',          // Card backgrounds
    border: '#D1D1D6',           // Subtle borders
  },
  fonts: {
    primary: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
    display: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  radius: {
    small: '8px',
    medium: '12px', 
    large: '16px',
    xlarge: '24px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },
  shadows: {
    small: '0 1px 3px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 12px rgba(0, 0, 0, 0.08)',
    large: '0 8px 24px rgba(0, 0, 0, 0.12)',
    xlarge: '0 16px 40px rgba(0, 0, 0, 0.16)',
  },
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
  }

  body {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    font-family: ${props => props.theme.fonts.primary};
    font-size: 17px;
    line-height: 1.47059;
    font-weight: 400;
    letter-spacing: -0.022em;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.display};
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.03em;
    margin: 0;
  }

  h1 {
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    letter-spacing: -0.04em;
  }

  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 600;
  }

  h3 {
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 600;
  }

  p {
    line-height: 1.6;
    margin: 0;
  }

  a {
    text-decoration: none;
    color: inherit;
    transition: ${props => props.theme.transition};
  }

  ul {
    list-style: none;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
    transition: ${props => props.theme.transition};
  }

  input, textarea {
    font-family: inherit;
  }

  .container {
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing.lg};
    
    @media (max-width: 768px) {
      padding: 0 ${props => props.theme.spacing.md};
    }
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundAlt};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.textLight};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.textMedium};
  }

  /* Selection styles */
  ::selection {
    background: ${props => props.theme.colors.primary};
    color: white;
  }

  /* Focus styles */
  :focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.main`
  flex: 1;
`;

function App() {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState("default");
  const isMobile = useIsMobile();

  useEffect(() => {
    // Only add mouse event listeners on non-mobile devices
    if (isMobile) return;
    
    const mouseMove = (e) => {
      setCursorPosition({
        x: e.clientX,
        y: e.clientY
      });
    };

    window.addEventListener("mousemove", mouseMove);

    return () => {
      window.removeEventListener("mousemove", mouseMove);
    };
  }, [isMobile]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <AppContainer>
          {!isMobile && (
            <Cursor mousePosition={cursorPosition} cursorVariant={cursorVariant} />
          )}
          <Navbar setCursorVariant={setCursorVariant} />
          <ContentContainer>
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<Home setCursorVariant={setCursorVariant} />} />
                <Route path="/chi-siamo" element={<About setCursorVariant={setCursorVariant} />} />
                <Route path="/servizi" element={<Services setCursorVariant={setCursorVariant} />} />
                <Route path="/servizio/:serviceId" element={<ServiceDetail setCursorVariant={setCursorVariant} />} />
                <Route path="/portfolio" element={<Portfolio setCursorVariant={setCursorVariant} />} />
                <Route path="/portfolio/:slug" element={<ProjectDetail setCursorVariant={setCursorVariant} />} />
                <Route path="/contatti" element={<Contact setCursorVariant={setCursorVariant} />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AnimatePresence>
          </ContentContainer>
          <Footer setCursorVariant={setCursorVariant} />
        </AppContainer>
      </Router>
    </ThemeProvider>
  );
}

export default App;
